import { useEffect, useMemo, useRef, useState } from "react"
import { useOnClickOutside } from "../../../hooks/useOnClickOutside"
import Button from "../../Assets/Button/Button"
import { PlusIcon } from "../../../icons/PlusIcon"
import { CrossIcon } from "../../../icons/CrossIcon"
import SelectReference from "../../SelectReference/SelectReference"
import OrderModalBlock from "../../Modals/OrderModal/OrderModalBlock"
import { TDirectory } from "../../../types/orderTypes"
import clsx from "clsx"
import useWindowSize from "../../../hooks/useWindowSize"
import ModalPortal from "../../Assets/ModalPortal/ModalPortal"
import { useScrollBlock } from "../../../hooks/useScrollBlock"
import { useTranslation } from "react-i18next"
import styles from "./OrderServiceReference.module.scss"

interface IBaseItem {
  id: string
  name: string
}

interface IUser extends IBaseItem {
  avatar_id?: string
}

interface IObject extends IBaseItem {
  path?: string
}

const OrderServiceReference = ({
  onSubmit,
  onClear,
  type,
  title,
  error,
  isRequired,
  isMultiple,
  // initItems для прокидывания данных из формика после сохранения в localStorage
  initItems,
  optionId,
}: {
  onSubmit: (ids: IUser[]) => void
  onClear: (ids: IUser[]) => void
  type: TDirectory
  title: string
  error?: boolean
  isRequired?: boolean
  isMultiple?: boolean
  initItems?: { id: string; name: string; avatar_id?: string }[]
  optionId?: string
}) => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const { isDesktop } = useWindowSize()
  const { allowScroll } = useScrollBlock()

  const [isDropDownOpen, setDropDownOpen] = useState<boolean>(false)
  const selectUserBtnRef = useRef<HTMLDivElement>(null)
  const [isDropDownTop, setDropDownTop] = useState<boolean>(false)

  useOnClickOutside(selectUserBtnRef, () => {
    if (!isDesktop) return
    if (isDropDownOpen) {
      setDropDownOpen(false)
    }
  })

  const [data, setData] = useState<IBaseItem[]>([])

  useEffect(() => {
    if (initItems && initItems.length > 0) setData(initItems)
  }, [initItems])

  const handleOpen = () => {
    if (selectUserBtnRef?.current && isDesktop) {
      const bottom = selectUserBtnRef.current.getBoundingClientRect().bottom
      type === "users"
        ? setDropDownTop(window.innerHeight - bottom < 250)
        : setDropDownTop(window.innerHeight - bottom < 350)
    }
    setDropDownOpen((prev) => !prev)
  }

  const handleClear = (id: string) => {
    setData((prev) => prev.filter((item: any) => item.id !== id))
    const findElems = data.filter((item: any) => item.id !== id)
    onClear(findElems || [])
  }

  const handleSubmit = (data: IUser[] | IObject[]) => {
    onSubmit(data)
    setData(data)
    setDropDownOpen(false)
    if (!isDesktop) allowScroll(true)
  }

  const isObjectList = useMemo(() => {
    return data.length > 0 && Object.keys(data[0]).includes("path_name")
  }, [data])

  return (
    <OrderModalBlock title={title} isRequired={isRequired} error={error}>
      <div ref={selectUserBtnRef} className={clsx(styles.block, error && [styles.error, "error"])}>
        {data?.map((item) => {
          return (
            <div key={item.id} className={styles.currentUser}>
              {isObjectList ? (
                <>
                  <div className={styles["object_item-text"]}>
                    <span className={styles.currentUserName}>{item?.name}</span>
                    {/* @ts-ignore */}
                    <span className={styles.pathName}>{item?.parentPath ?? item.path_name}</span>
                  </div>

                  <button type={"button"} className={styles.currentUserBtn} onClick={() => handleClear(item.id)}>
                    <CrossIcon />
                  </button>
                </>
              ) : (
                <>
                  <span className={styles.currentUserName}>{item?.name}</span>
                  <button type={"button"} className={clsx(styles.currentUserBtn)} onClick={() => handleClear(item.id)}>
                    <CrossIcon />
                  </button>
                </>
              )}
            </div>
          )
        })}
        {(isMultiple || !data?.length) && (
          <Button
            type={"button"}
            txt={
              <>
                <PlusIcon />
                {t("add")}
              </>
            }
            mode={"light"}
            className={styles.btn}
            onClick={handleOpen}
          />
        )}
        {isDropDownOpen && (
          <>
            {isDesktop ? (
              <SelectReference
                type={type}
                list={data}
                isTop={isDropDownTop}
                isMultiple={isMultiple}
                onSubmit={(data) => handleSubmit(data)}
                selectUserBtnRef={selectUserBtnRef}
                optionId={optionId}
              />
            ) : (
              <ModalPortal
                isOpen={isDropDownOpen}
                setIsOpen={setDropDownOpen}
                header={<h2 className={"modal__title"}>{title}</h2>}
                className={styles.modal}
              >
                <SelectReference
                  type={type}
                  list={data}
                  isMultiple={isMultiple}
                  onSubmit={(data) => handleSubmit(data)}
                  onClose={() => {
                    setDropDownOpen(false)
                    allowScroll(true)
                  }}
                  optionId={optionId}
                />
              </ModalPortal>
            )}
          </>
        )}
      </div>
    </OrderModalBlock>
  )
}

export default OrderServiceReference
